/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "either" }] */
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { isBrandRefreshEnabled } from '../../shared/utilities/brandRefresh';
import { getDonationStores, submitDonation } from '../../shared/repos/graphql/donations';
import ReCAPTCHAForm from '../../components/ReCaptcha/ReCaptcha';
import EventAddress from './EventAddress';
import { initialForm, minimumEventDate, validateStep } from './donationForm';
import styles from './Donations.module.css';
import Link from '../../components/LinkWithCountry/LinkWithCountry';

const fieldSteps = {
  contact_name: 1, company_name: 1, store: 1, email: 1, phone: 1,
  org_type: 2, n_501c3_no: 2, event_type: 2, event_location: 2, event_location_2: 2,
};
const stepTitles = ['Contact', 'Event', 'Submit'];

const Donations = () => {
  const refreshed = isBrandRefreshEnabled();
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaVersion, setCaptchaVersion] = useState(0);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const pending = useRef(false);
  const heading = useRef(null);
  const summary = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDonationStores().then(response => {
      if (active) setStores(response.data.donationStores);
    }).catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);
  useEffect(() => {
    if (Object.keys(errors).length && summary.current) summary.current.focus();
    else if (heading.current) heading.current.focus();
  }, [step, errors, success]);

  const change = event => {
    const { name, value, type, checked } = event.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };
  const selectAddress = (address, lat, lng) => {
    setForm(current => ({ ...current, event_location: address, autocompleteLat: lat, autocompleteLng: lng }));
  };
  const error = field => errors[field] && <p className={styles.error} id={`donation-${field}-error`}>{errors[field]}</p>;
  const field = (name, label, type = 'text', extra = {}) => (
    <div className={styles.field}>
      <label htmlFor={`donation-${name}`}>{label}</label>
      <input
        id={`donation-${name}`}
        name={name}
        type={type}
        value={form[name]}
        onChange={change}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `donation-${name}-error` : undefined}
        autoComplete={extra.autoComplete}
        maxLength={extra.maxLength}
        min={extra.min}
        inputMode={extra.inputMode}
      />
      {error(name)}
    </div>
  );
  const textarea = (name, label, maxLength = 5000) => (
    <div className={styles.field}>
      <label htmlFor={`donation-${name}`}>{label}</label>
      <textarea
        id={`donation-${name}`}
        name={name}
        value={form[name]}
        onChange={change}
        rows={4}
        maxLength={maxLength}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `donation-${name}-error` : undefined}
      />
      {error(name)}
    </div>
  );
  const attach = event => {
    const uploadInput = event.target;
    const selected = uploadInput.files[0];
    setFile(null);
    setErrors({});
    if (!selected) return;
    if (selected.size > 2048 * 1024 || !/\.(jpe?g|png|pdf|docx?)$/i.test(selected.name)) {
      setErrors({ file: 'Choose a JPG, PNG, PDF, DOC or DOCX file up to 2 MB.' });
      uploadInput.value = '';
      return;
    }
    setFile(selected);
  };
  const next = () => {
    const validation = validateStep(step, form);
    setErrors(validation);
    if (!Object.keys(validation).length) setStep(current => current + 1);
  };
  const send = async event => {
    event.preventDefault();
    if (pending.current) return;
    if (step !== 3) { next(); return; }
    for (let index = 1; index <= 3; index += 1) {
      const validation = validateStep(index, form);
      if (Object.keys(validation).length) {
        setErrors(validation);
        setStep(index);
        return;
      }
    }
    if (!captchaToken) { setErrors({ captchaToken: 'Please complete verification.' }); return; }
    pending.current = true;
    setSending(true);
    setErrors({});
    try {
      const response = await submitDonation({ ...form, phone: form.phone.replace(/\D/g, ''), captchaToken }, file);
      const result = response.data.submitDonation;
      if (result.success) {
        setSuccess(true);
        setFile(null);
        setForm(initialForm);
      } else {
        const validation = {};
        result.errors.forEach(item => { validation[item.field] = item.message; });
        setErrors(Object.keys(validation).length ? validation : { form: 'We could not send your request.' });
        setStep(fieldSteps[result.errors[0]?.field] || 3);
      }
    } catch (requestError) {
      setErrors({ form: 'We could not confirm submission. Please check your connection before trying again.' });
    } finally {
      pending.current = false;
      setSending(false);
      setCaptchaToken('');
      setCaptchaVersion(current => current + 1);
    }
  };
  const actionLabel = step === 3 ? 'Submit request' : 'Next';
  const buttonLabel = sending ? 'Sending…' : actionLabel;
  return (
    <main className={styles.page} data-brand-refresh={refreshed ? 'true' : 'false'}>
      <Helmet>
        <title>Donations | Insomnia Cookies</title>
        <meta name="description" content="Review our eligibility guidelines and request a cookie donation for your community event." />
      </Helmet>
      <div className={styles.layout}>
        <section className={styles.panel} aria-labelledby="donations-title">
          <h1 id="donations-title" tabIndex={-1} ref={heading}>{success ? 'Request submitted' : 'Donation Request'}</h1>
          {success ? (
            <div role="status">
              <p>Your message was sent successfully!</p>
              <p>Your request will be processed by our marketing department. Due to the volume of requests, we cannot approve them all. You will only be notified if your request is approved.</p>
            </div>
          ) : (
            <>
              <ol className={styles.progress} aria-label="Donation request steps">
                {stepTitles.map((title, index) => (
                  <li
                    key={title}
                    aria-current={step === index + 1 ? 'step' : undefined}
                    data-complete={step > index + 1 ? 'true' : 'false'}
                  >
                    <span>{index + 1}</span>
                    {title}
                  </li>
                ))}
              </ol>
              {Object.keys(errors).length > 0 && (
                <div role="alert" tabIndex={-1} ref={summary} className={styles.summary}>
                  <h2>Please review your request</h2>
                  <ul>{Object.entries(errors).map(([name, message]) => <li key={name}>{message}</li>)}</ul>
                </div>
              )}
              {loading && <p role="status">Loading pickup stores…</p>}
              {loadError && (
              <div role="alert">
                <p>Pickup stores could not be loaded.</p>
                <button type="button" onClick={() => setAttempt(current => current + 1)}>Retry loading stores</button>
              </div>
)}
              {!loading && !loadError && stores.length === 0 && <p role="alert">No pickup stores are available. Please try again later.</p>}
              {!loading && !loadError && stores.length > 0 && (
                <form onSubmit={send} noValidate>
                  <fieldset disabled={sending}>
                    <legend>{`Step ${step}: ${stepTitles[step - 1]}`}</legend>
                    {step === 1 && (
                      <>
                        {field('contact_name', 'Full name', 'text', { autoComplete: 'name', maxLength: 255 })}
                        {field('company_name', 'Organization name', 'text', { autoComplete: 'organization', maxLength: 255 })}
                        <div className={styles.field}>
                          <label htmlFor="donation-store">Select a store for pickup</label>
                          <select
                            id="donation-store"
                            name="store"
                            value={form.store}
                            onChange={change}
                            aria-invalid={Boolean(errors.store)}
                            aria-describedby={errors.store ? 'donation-store-error' : undefined}
                          >
                            <option value="">Select a store for pickup</option>
                            {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
                          </select>
                          {error('store')}
                        </div>
                        {field('email', 'Email address', 'email', { autoComplete: 'email' })}
                        {field('phone', 'Cell phone', 'tel', { autoComplete: 'tel-national' })}
                      </>
                    )}
                    {step === 2 && (
                      <>
                        {textarea('org_type', 'Brief description of organization')}
                        <label className={styles.checkbox} htmlFor="donation-nonprofit">
                          <input id="donation-nonprofit" type="checkbox" name="n_501C3" checked={form.n_501C3} onChange={change} />
501C3 organization
                        </label>
                        {form.n_501C3 && field('n_501c3_no', '501C3 number', 'text', { maxLength: 255 })}
                        {textarea('event_type', 'Type of event and brief description')}
                        <EventAddress value={form.event_location} onSelect={selectAddress} invalid={Boolean(errors.event_location)} />
                        {error('event_location')}
                        {field('event_location_2', 'Event location line 2 (optional)', 'text', { maxLength: 50 })}
                      </>
                    )}
                    {step === 3 && (
                      <>
                        {textarea('organization_promoting', 'How are you promoting the event and what recognition opportunities are available in exchange for a donation (e.g. logo inclusion or social media posts)?')}
                        <p>At least 50 characters.</p>
                        {field('event_date', 'Event date', 'date', { min: minimumEventDate() })}
                        {field('event_time', 'Event time', 'time')}
                        {field('expected_participants', 'Expected number of participants', 'text', { inputMode: 'decimal' })}
                        <div className={styles.field}>
                          <label htmlFor="donation-file">
                            Attachment (optional)
                            <input
                              id="donation-file"
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                              onChange={attach}
                              aria-describedby="donation-file-help"
                              aria-invalid={Boolean(errors.file)}
                            />
                          </label>
                          <p id="donation-file-help">JPG, PNG, PDF, DOC or DOCX, up to 2 MB. Uploaded when you submit.</p>
                          {file && (
                          <p role="status">
Selected:
                            {file.name}
                          </p>
)}
                          {error('file')}
                        </div>
                        <ReCAPTCHAForm key={captchaVersion} size="compact" setToken={setCaptchaToken} />
                        {error('captchaToken')}
                      </>
                    )}
                    <div className={styles.actions}>
                      {step > 1 && <button type="button" onClick={() => { setErrors({}); setStep(current => current - 1); }}>Back</button>}
                      <button type="submit">{buttonLabel}</button>
                    </div>
                  </fieldset>
                </form>
              )}
              <p className={styles.consent}>
I consent to receive SMS from Insomnia Cookies. Reply STOP to opt-out; Reply HELP for assistance; Message and data rates apply; Messaging frequency may vary. You can find the full
                <Link to="/privacy-policy">privacy policy here.</Link>
              </p>
            </>
          )}
        </section>
        <aside className={styles.panel} aria-labelledby="donation-guidelines">
          <h2 id="donation-guidelines">Cookie Donations</h2>
          <p>Insomnia Cookies&apos; desire to be a part of the community also means we love participating in community events. In the past few years we have donated many cookies to local charities and organizations for their efforts. If you are interested in having Insomnia Cookies donate cookies or a gift card to your event, please review the eligibility guidelines and submit the request below.</p>
          <ul>
            <li><strong>Requests must be received at least 4 weeks prior to the event date being requested in order to be considered.</strong></li>
            <li>Our bakeries can only accommodate pickup requests during store operating hours</li>
            <li>To ensure the quality of our product, we only allow cookies to be picked up on the day of the event and do not allow our cookies to be resold.</li>
            <li>All gift card donations are mailed directly from Insomnia Cookies’ corporate office and require two weeks to ship</li>
            <li>Unfortunately due to the large number of requests we receive daily, we are unable to fulfill them all and can only notify organizations that are approved.</li>
          </ul>
        </aside>
      </div>
    </main>
  );
};

export default Donations;
